import React, { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useInView } from "framer-motion";
import {
  FEATURED_PRODUCT,
  GRID_PRODUCTS,
  type ProductCard,
} from "../data/products/productsRegistry";

// ─── Animation ────────────────────────────────────────────────────────
const fadeUp = {
  hidden: { opacity: 0, y: 28 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.65,
      ease: [0.22, 1, 0.36, 1] as const,
      delay: i * 0.07,
    },
  }),
};

function InView({
  children,
  delay = 0,
  className,
}: {
  children: React.ReactNode;
  delay?: number;
  className?: string;
}) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" as any });
  return (
    <motion.div
      ref={ref}
      className={className}
      initial="hidden"
      animate={inView ? "show" : "hidden"}
      custom={delay}
      variants={fadeUp}
    >
      {children}
    </motion.div>
  );
}

// ─── Grid product card ────────────────────────────────────────────────
function ProductGridCard({
  product,
  delay,
}: {
  product: ProductCard;
  delay: number;
}) {
  return (
    <InView delay={delay} className="bg-white group">
      <div className="relative overflow-hidden aspect-[3/2]">
        <img
          src={product.image}
          alt={product.title}
          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
        />
      </div>
      <div className="p-7">
        <span className="text-[10px] font-semibold uppercase tracking-widest text-itl-blue block mb-3">
          {product.tag}
        </span>
        <h3 className="font-serif text-xl font-light text-itl-navy mb-3 group-hover:text-itl-blue transition-colors">
          {product.title}
        </h3>
        <p className="text-gray-500 text-[13px] leading-relaxed line-clamp-3 mb-5">
          {product.excerpt}
        </p>
        <div className="flex flex-wrap gap-1.5 mb-5">
          {product.techStack.map((t) => (
            <span
              key={t}
              className="px-2 py-0.5 bg-gray-50 border border-gray-100 text-gray-400 text-[10px] uppercase tracking-wide"
            >
              {t}
            </span>
          ))}
        </div>
        <div className="flex gap-3">
          <Link
            to={product.demoHref}
            className="text-[11px] font-semibold uppercase tracking-widest text-itl-blue hover:text-itl-navy transition-colors"
          >
            Demo →
          </Link>
          <Link
            to={product.href}
            className="text-[11px] font-semibold uppercase tracking-widest text-gray-400 hover:text-itl-navy transition-colors"
          >
            Details →
          </Link>
        </div>
      </div>
    </InView>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────
export default function Products() {
  return (
    <>
      {/* ── Hero ── */}
      <div className="bg-itl-navy pt-40 pb-20">
        <div className="max-w-screen-xl mx-auto px-6 lg:px-12">
          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="text-[11px] font-semibold uppercase tracking-[0.25em] text-itl-blue mb-4"
          >
            Products &amp; Demos
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1], delay: 0.2 }}
            className="font-serif text-5xl lg:text-6xl font-light text-white max-w-2xl leading-tight mb-5"
          >
            Work that speaks for itself.
          </motion.h1>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="text-white/60 text-base max-w-lg leading-relaxed"
          >
            From proprietary products to client-built platforms — explore what
            we've shipped.
          </motion.p>
        </div>
      </div>

      {/* ── Featured product ── */}
      {/* FEATURED_PRODUCT is always the entry with featured: true in productsRegistry.ts */}
      <section className="bg-white py-16">
        <div className="max-w-screen-xl mx-auto px-6 lg:px-12">
          <InView>
            <div className="grid grid-cols-1 lg:grid-cols-2 border border-gray-100">
              <div className="relative overflow-hidden aspect-[4/3] lg:aspect-auto">
                <img
                  src={FEATURED_PRODUCT.image}
                  alt={FEATURED_PRODUCT.title}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-itl-navy/10" />
              </div>
              <div className="p-10 lg:p-14 flex flex-col justify-between">
                <div>
                  <span className="inline-block px-3 py-1 bg-itl-blue/10 text-itl-blue text-[10px] font-semibold uppercase tracking-widest mb-5">
                    {FEATURED_PRODUCT.tag}
                  </span>
                  <h2 className="font-serif text-4xl font-light text-itl-navy leading-snug mb-4">
                    {FEATURED_PRODUCT.title}
                  </h2>
                  <p className="text-gray-500 leading-relaxed mb-8">
                    {FEATURED_PRODUCT.excerpt}
                  </p>
                  <div className="flex flex-wrap gap-2 mb-8">
                    {FEATURED_PRODUCT.techStack.map((t) => (
                      <span
                        key={t}
                        className="px-3 py-1 border border-gray-200 text-gray-500 text-[11px] uppercase tracking-wide"
                      >
                        {t}
                      </span>
                    ))}
                  </div>
                </div>
                <div className="flex gap-4">
                  <Link
                    to={FEATURED_PRODUCT.demoHref}
                    className="px-7 py-3 bg-itl-blue text-white text-[11px] font-semibold uppercase tracking-widest hover:bg-itl-navy transition-colors"
                  >
                    Live Demo
                  </Link>
                  <Link
                    to={FEATURED_PRODUCT.href}
                    className="px-7 py-3 border border-itl-navy text-itl-navy text-[11px] font-semibold uppercase tracking-widest hover:bg-itl-navy hover:text-white transition-colors"
                  >
                    Full Description
                  </Link>
                </div>
              </div>
            </div>
          </InView>
        </div>
      </section>

      {/* ── Grid of remaining products ── */}
      {/* GRID_PRODUCTS updates automatically as you add entries to productsRegistry.ts */}
      <section className="bg-[#f7f7f5] py-16">
        <div className="max-w-screen-xl mx-auto px-6 lg:px-12">
          <InView className="mb-10">
            <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-gray-400">
              More from Incubator Tech
            </p>
          </InView>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-gray-200">
            {GRID_PRODUCTS.map((product, i) => (
              <ProductGridCard
                key={product.slug}
                product={product}
                delay={i * 0.08}
              />
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="bg-itl-navy py-24 text-center">
        <InView>
          <p className="text-[11px] font-semibold uppercase tracking-[0.25em] text-itl-blue mb-5">
            Have a Project in Mind?
          </p>
          <h2 className="font-serif text-4xl font-light text-white max-w-xl mx-auto leading-snug mb-8">
            Let's build your product next.
          </h2>
          <Link
            to="/contact"
            className="inline-block px-10 py-4 bg-itl-blue text-white text-[12px] font-semibold uppercase tracking-widest hover:bg-blue-600 transition-colors"
          >
            Start a Project
          </Link>
        </InView>
      </section>
    </>
  );
}
